import React, { useCallback, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
	downloadStudentApplicationDocument,
	fetchStudentApplicationDocumentsStatus,
	refreshStudentApplicationDocuments,
	uploadStudentApplicationDocument,
} from 'api/applicationDocuments'
import { validateUploadStudentDocumentFile } from 'widgets/UploadStudentDocumentModal/hooks/useUploadStudentDocumentModal'
import NudgeStudentModal from 'widgets/NudgeStudentModal/NudgeStudentModal'
import PdfPreviewPortal from '../Checklist/components/PdfPreviewPortal/PdfPreviewPortal'
import PanelHeader from '../shared/PanelHeader/PanelHeader'
import PanelAssignButton from '../shared/PanelAssignButton/PanelAssignButton'
import ScrollArea from '../shared/ScrollArea/ScrollArea'
import DocumentChecklistRow from '../shared/DocumentChecklistRow/DocumentChecklistRow'
import ListRow from '../shared/ListRow/ListRow'
import { useDocuments } from './hooks/useDocuments'
import {
	getDocumentNudgeCooldownRemainingMs,
	isDocumentNudgeOnCooldown,
} from '../../utils/documentNudgeCooldown'
import { useSafeErrorBanner } from 'shared/components/ErrorBanner/useSafeErrorBanner'
import panel from '../shared/styles/panel.module.scss'
import list from '../shared/ListRow/ListRow.module.scss'
import s from './Documents.module.scss'

const Documents = observer(({
	application,
	onAssignDocument,
	onReuploadDocument,
	onAssignAllDocuments,
}) => {
	const { isLoading, groups, progress, reload } = useDocuments(application)
	const { showErrorSafe } = useSafeErrorBanner()
	const [refreshStatus, setRefreshStatus] = useState('idle')
	// Held in a ref so unmounting mid-poll clears the interval; otherwise it
	// keeps running and calls setState on a component that is gone.
	const refreshTimerRef = useRef(null)

	useEffect(
		() => () => {
			if (refreshTimerRef.current) window.clearInterval(refreshTimerRef.current)
		},
		[],
	)
	const [uploadingRequirementId, setUploadingRequirementId] = useState('')
	const [nudgeTarget, setNudgeTarget] = useState(null)
	const [nudgeCooldownTick, setNudgeCooldownTick] = useState(0)

	useEffect(() => {
		const items = groups.flatMap(group => group.items || [])
		const cooling = items.filter(item =>
			isDocumentNudgeOnCooldown(item.lastNudgeAt),
		)
		if (!cooling.length) return undefined
		const remaining = Math.min(
			...cooling.map(item =>
				getDocumentNudgeCooldownRemainingMs(item.lastNudgeAt),
			),
		)
		if (remaining <= 0) {
			setNudgeCooldownTick(value => value + 1)
			return undefined
		}
		const timer = window.setTimeout(
			() => setNudgeCooldownTick(value => value + 1),
			remaining + 50,
		)
		return () => window.clearTimeout(timer)
	}, [groups, nudgeCooldownTick])
	// Ask the backend to look up this course's document requirements, then poll
	// until the worker finishes and reload the checklist.
	//
	// Merged into the student's existing requirements server-side, so a document
	// they have already uploaded against keeps its id and its file.
	const onRefreshRequirements = useCallback(async () => {
		const studentId = application?.studentId
		const applicationTargetId =
			application?.applicationTargetId || application?.id
		if (!studentId || !applicationTargetId) return

		setRefreshStatus('searching')
		try {
			await refreshStudentApplicationDocuments(studentId, applicationTargetId)
		} catch (err) {
			setRefreshStatus('idle')
			showErrorSafe(err, 'Could not start the document refresh.')
			return
		}

		const startedAt = Date.now()
		if (refreshTimerRef.current) window.clearInterval(refreshTimerRef.current)
		const timer = window.setInterval(async () => {
			// The worker times out at 90s and may retry once; stop well after that
			// rather than leaving an interval running for the rest of the session.
			if (Date.now() - startedAt > 5 * 60 * 1000) {
				window.clearInterval(timer)
				refreshTimerRef.current = null
				setRefreshStatus('idle')
				return
			}
			let res
			try {
				res = await fetchStudentApplicationDocumentsStatus(
					studentId,
					applicationTargetId,
				)
			} catch {
				window.clearInterval(timer)
				refreshTimerRef.current = null
				setRefreshStatus('idle')
				return
			}
			if (res?.status === 'searching') return

			window.clearInterval(timer)
			refreshTimerRef.current = null
			setRefreshStatus('idle')
			if (res?.status === 'rejected' && res?.error) {
				showErrorSafe(new Error(res.error), 'The document refresh failed.')
				return
			}
			await reload()
		}, 4000)
		refreshTimerRef.current = timer
	}, [application, reload, showErrorSafe])

	const [pdfPreview, setPdfPreview] = useState({
		open: false,
		url: '',
		title: '',
	})

	const studentId = application?.studentId
	const studentName = application?.studentName || 'Student'

	const handleAssign = useCallback(
		item => {
			onAssignDocument?.({
				id: item.id,
				requirementId: item.requirementId,
				name: item.title,
				title: item.title,
			})
		},
		[onAssignDocument],
	)

	const handleReupload = useCallback(
		item => {
			onReuploadDocument?.({
				id: item.id,
				requirementId: item.requirementId,
				name: item.title,
				title: item.title,
			})
		},
		[onReuploadDocument],
	)

	const handleDirectUpload = useCallback(
		async (item, file) => {
			const rid = String(item?.requirementId || '').trim()
			if (!studentId || !rid) return
			const clientErr = validateUploadStudentDocumentFile(file)
			if (clientErr) {
				showErrorSafe(clientErr)
				return
			}
			setUploadingRequirementId(rid)
			try {
				await uploadStudentApplicationDocument(studentId, rid, file)
				reload()
			} catch (e) {
				showErrorSafe(
					e?.response?.data?.error ||
						e?.message ||
						'Upload failed. Please try again.',
				)
			} finally {
				setUploadingRequirementId('')
			}
		},
		[studentId, reload, showErrorSafe],
	)

	const handleView = useCallback(
		async item => {
			const fileId = item?.fileId
			if (!studentId || !fileId) return
			try {
				const blob = await downloadStudentApplicationDocument(studentId, fileId)
				const objectUrl = window.URL.createObjectURL(blob)
				setPdfPreview({
					open: true,
					url: objectUrl,
					title: item?.title || 'Document',
				})
			} catch (e) {
				showErrorSafe('Failed to open document.')
				console.error(e)
			}
		},
		[studentId, showErrorSafe],
	)

	const closePdfPreview = useCallback(() => {
		setPdfPreview(prev => {
			if (prev?.url && String(prev.url).startsWith('blob:')) {
				try {
					window.URL.revokeObjectURL(prev.url)
				} catch {}
			}
			return { open: false, url: '', title: '' }
		})
	}, [])

	return (
		<>
			<section className={panel.panel}>
				<PanelHeader
				title="Documents"
				icon="description"
				progress={progress}
				right={
					<>
						<PanelAssignButton
							label={
								refreshStatus === 'searching' ? 'Checking\u2026' : 'Refresh'
							}
							onClick={onRefreshRequirements}
							disabled={refreshStatus === 'searching'}
						/>
						<PanelAssignButton
							label="Assign"
							onClick={onAssignAllDocuments}
						/>
					</>
				}
			/>
				<ScrollArea className={list.rows}>
					{isLoading ? (
						groups.map(group => (
							<div key={group.id} className={s.group}>
								<h3 className={s.groupTitle}>{group.title}</h3>
								{group.items.map(item => (
									<ListRow
										key={item.requirementId}
										title=""
										isLoading
										className={list['row--docs']}
									/>
								))}
							</div>
						))
					) : (
						groups.map(group => (
							<div key={group.id} className={s.group}>
								<h3 className={s.groupTitle}>{group.title}</h3>
								{group.items.map(item => (
									<DocumentChecklistRow
										key={`${item.requirementId}-${nudgeCooldownTick}`}
										item={item}
										onAssign={handleAssign}
										onUploadClick={handleDirectUpload}
										onNudge={row => setNudgeTarget(row)}
										onReupload={handleReupload}
										onView={handleView}
										nudgeOnCooldown={isDocumentNudgeOnCooldown(
											item.lastNudgeAt,
										)}
										isUploading={
											uploadingRequirementId === item.requirementId
										}
									/>
								))}
							</div>
						))
					)}
				</ScrollArea>
			</section>

			<NudgeStudentModal
				isOpen={Boolean(nudgeTarget)}
				onClose={() => setNudgeTarget(null)}
				studentId={studentId}
				studentName={studentName}
				documentTitle={nudgeTarget?.title || 'Document'}
				requirementId={nudgeTarget?.requirementId || ''}
				applicationDeadlineRaw={
					application?.deadline || application?.applicationDeadline || ''
				}
				onSuccess={() => {
					setNudgeTarget(null)
					reload()
				}}
			/>

			<PdfPreviewPortal
				open={pdfPreview.open}
				url={pdfPreview.url}
				title={pdfPreview.title}
				onClose={closePdfPreview}
			/>
		</>
	)
})

export default Documents
